import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { exportToCsv } from "@/lib/csv";
import { Download, Diamond } from "lucide-react";

export const allocType = (a) => ({
  to_subagent: `Sent to ${a.counterparty || "sub-agent"}`,
  from_subagent: `Taken back from ${a.counterparty || "sub-agent"}`,
  from_reseller: `Received from ${a.counterparty || "reseller"}`,
  reclaimed: `Taken back by ${a.counterparty || "reseller"}`,
}[a.type] || (a.amount < 0 ? "Taken back" : "Credited"));

export default function ResellerAllocations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  useEffect(() => {
    api.get("/reseller/allocations")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const inRange = (iso) => {
    const t = new Date(iso).getTime();
    if (start && t < new Date(start).getTime()) return false;
    if (end && t > new Date(end).getTime()) return false;
    return true;
  };
  const filtered = rows.filter((a) => inRange(a.created_at));

  const exportCsv = () => exportToCsv("reseller-allocations", filtered, [
    { header: "Timestamp", accessor: (a) => new Date(a.created_at).toLocaleString() },
    { header: "Platform", accessor: (a) => a.platform_name || a.platform },
    { header: "Diamonds", accessor: (a) => a.amount },
    { header: "Type", accessor: (a) => allocType(a) },
    { header: "Balance after", accessor: (a) => a.balance_after ?? "" },
    { header: "Note", accessor: (a) => a.note },
    { header: "By", accessor: (a) => a.admin_email },
  ]);

  return (
    <div className="max-w-5xl">
      <div className="flex items-center gap-2 mb-1">
        <Diamond className="w-5 h-5 text-zinc-700" />
        <h1 className="font-display text-xl font-semibold text-zinc-900">Diamond allocation history</h1>
      </div>
      <p className="text-sm text-zinc-500 mb-4">Every diamond credit, transfer or adjustment on your account, with your balance after each entry.</p>

      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="reseller-alloc-controls">
        <label className="text-xs text-zinc-500">From</label>
        <input type="datetime-local" value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)}
          className="h-9 px-2 text-sm rounded-md border border-zinc-300 bg-white" data-testid="reseller-alloc-start" />
        <label className="text-xs text-zinc-500">To</label>
        <input type="datetime-local" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)}
          className="h-9 px-2 text-sm rounded-md border border-zinc-300 bg-white" data-testid="reseller-alloc-end" />
        {(start || end) && <button onClick={() => { setStart(""); setEnd(""); }} className="text-xs text-zinc-500 hover:text-zinc-800">Clear</button>}
        <button onClick={exportCsv} data-testid="reseller-alloc-export-csv"
          className="ml-auto inline-flex items-center gap-1 h-9 px-3 text-sm rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 font-medium">
          <Download className="w-4 h-4" /> Download CSV
        </button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              {["Timestamp", "Platform", "Diamonds", "Type", "Balance after", "Note", "By"].map((h) => (
                <th key={h} className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-5 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="reseller-alloc-table">
            {filtered.length === 0 && (
              <tr><td colSpan="7" className="px-5 py-12 text-center text-zinc-400 text-sm">{loading ? "Loading…" : "No allocations in this range"}</td></tr>
            )}
            {filtered.map((a) => (
              <tr key={a.id} className="border-b border-zinc-100 last:border-b-0 text-sm">
                <td className="px-5 py-3 whitespace-nowrap text-zinc-600">{new Date(a.created_at).toLocaleString()}</td>
                <td className="px-5 py-3 text-zinc-700">{a.platform_name || a.platform}</td>
                <td className={`px-5 py-3 tabular-nums font-mono font-medium ${a.amount < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                  {a.amount < 0 ? "−" : "+"}{Math.abs(a.amount).toLocaleString()}
                </td>
                <td className="px-5 py-3 text-xs text-zinc-500">{allocType(a)}</td>
                <td className="px-5 py-3 tabular-nums font-mono text-zinc-700" data-testid={`alloc-balance-after-${a.id}`}>{a.balance_after != null ? a.balance_after.toLocaleString() : "—"}</td>
                <td className="px-5 py-3 text-xs text-zinc-500">{a.note || "—"}</td>
                <td className="px-5 py-3 text-xs text-zinc-400 font-mono">{a.admin_email || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
